/* File: frontend/src/services/consiliumJob.js */
/* Опрос состояния работы консилиума (задача 260907-ktd). */
/*
   Чистый модуль: ни React, ни прямого fetch/setTimeout. Всё внешнее
   внедряется параметрами — тот же приём, что уже применён к consiliumBridge.js,
   и ровно затем, чтобы сторож прогонял этот файл НАСТОЯЩИМ node, а не читал
   его глазами.
*/

export const POLL_INTERVAL_MS = 5000;

/* 120 × 5 с = 10 минут.
   🔴 Предел существует затем, что незакрытая вкладка иначе опрашивает вечно.
   Замер 07.09.2026: работа в режиме вопроса заняла 324 с (5 мин 24 с) — предел
   взят с запасом почти вдвое. Для режима дела длительность НЕ измерялась
   никогда, поэтому запас общий; с этой правки длительность начнёт измеряться
   сама (duration_seconds в ответе опроса). */
export const MAX_POLL_ATTEMPTS = 120;

export const JOB_URL_PREFIX = '/api/v1/ai-paralegal/consilium/jobs/';

const defaultSleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Опрашивает состояние работы до определённости или до исчерпания предела.
 *
 * Исходы (пять, и ни один не притворяется другим):
 *   done      — работа завершена, payload содержит result и duration_seconds
 *   failed    — работа провалилась, payload содержит error
 *   unknown   — состояние НЕ ИЗВЕСТНО: записи нет либо исполнитель молчит
 *   gave_up   — предел опросов исчерпан; про саму работу мы ничего не узнали
 *   cancelled — ушли со страницы
 *
 * 🔴 Отказ измерителя (сеть отвалилась, 500 от сервера) НЕ выдаётся за
 * состояние работы: это просто потраченная попытка, опрос продолжается.
 */
export async function pollConsiliumJob({
  jobId,
  fetchImpl,
  sleepImpl = defaultSleep,
  isCancelled,
  onState,
  intervalMs = POLL_INTERVAL_MS,
  maxAttempts = MAX_POLL_ATTEMPTS,
}) {
  let attempts = 0;

  while (attempts < maxAttempts) {
    if (isCancelled && isCancelled()) {
      return { outcome: 'cancelled', attempts };
    }
    attempts += 1;

    let res = null;
    let transientError = null;
    try {
      res = await fetchImpl(JOB_URL_PREFIX + jobId);
    } catch (err) {
      transientError = err;
    }

    if (transientError === null && res && res.status === 404) {
      /* Чужая или несуществующая работа — опрашивать дальше нечего. */
      return { outcome: 'unknown', attempts, payload: { reason: 'not_found' } };
    }

    if (transientError !== null || !res || !res.ok) {
      /* Прибор не ответил. Это не «идёт» и не «провалилась» — это ничего. */
      if (onState) {
        onState({
          state: 'running',
          transient: true,
          detail: transientError ? String(transientError.message || transientError)
                                 : `HTTP ${res && res.status}`,
        });
      }
      await sleepImpl(intervalMs);
      continue;
    }

    let body = null;
    try {
      body = await res.json();
    } catch (err) {
      if (onState) onState({ state: 'running', transient: true, detail: 'ответ не разобран' });
      await sleepImpl(intervalMs);
      continue;
    }

    if (onState) onState(body);

    if (body && body.state === 'done') {
      return { outcome: 'done', attempts, payload: body };
    }
    if (body && body.state === 'failed') {
      return { outcome: 'failed', attempts, payload: body };
    }
    if (body && body.state === 'unknown') {
      return { outcome: 'unknown', attempts, payload: body };
    }

    await sleepImpl(intervalMs);
  }

  return { outcome: 'gave_up', attempts };
}

const consiliumJob = { pollConsiliumJob, POLL_INTERVAL_MS, MAX_POLL_ATTEMPTS, JOB_URL_PREFIX };
export default consiliumJob;
